import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { CloseCircleIcon } from "icons/payments/CloseCircleIcon";
import { AddCircleIcon } from "icons/payments/AddCircleIcon";
import { SearchIcon } from "icons/payments/SearchIcon";
import "../../assets/css/CommenInput.css";
import { AddCircleIconBlue } from "utils/icon";
import { getShellZoom } from "utils/shellZoom";

// PROPS TYPE
interface SelectAllDropDownProps {
  label: string;
  options: any;
  checked?: any;
  setChecked?: any;
  isRight?: boolean;
  isSearchDisplay?: boolean;
  budgetCalculator?: boolean;
  defaultChecked?: any[];
  onOpen?: () => void;
}

/* ------------------------------------- SELECT ALL ------------------------------------- */

const SelectAllDropDown = (props: SelectAllDropDownProps) => {
  const {
    label = "label",
    options,
    checked,
    setChecked,
    isRight = false,
    isSearchDisplay = false,
    budgetCalculator,
    defaultChecked = [],
    onOpen = () => {},
  } = props;

  const dropDownRef = useRef<any>();
  const portalDropDownRef = useRef<any>();

  const [isOpenDropDown, setIsOpenDropDown] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState("");
  const [filterData, setFilterData] = useState(options);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>(
    {
      top: 0,
      left: 0,
    }
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropDownRef.current &&
        !dropDownRef.current.contains(e.target) &&
        (!portalDropDownRef.current ||
          !portalDropDownRef.current.contains(e.target))
      ) {
        setIsOpenDropDown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setFilterData(options);
  }, [options]);

  useEffect(() => {
    if (searchInput) {
      const filteredData = options.filter((el: any) =>
        el.name?.toLowerCase().includes(searchInput?.toLowerCase())
      );
      setFilterData(filteredData);
    } else {
      setFilterData(options);
    }
  }, [searchInput, options]);

  useEffect(() => {
    if (defaultChecked.length > 0 && checked.length === 0) {
      setChecked(defaultChecked);
    }
  }, [defaultChecked, checked.length, setChecked]);

  const updateDropdownPosition = () => {
    if (!dropDownRef.current) return;

    // Portalled and scaled: the measured rect is real px, the inline
    // coordinates are read as design px. See utils/shellZoom.
    const zoom = getShellZoom(dropDownRef.current);
    const rect = dropDownRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth / zoom;
    const width = isRight ? 260 : 200;
    const maxLeft = viewportWidth - width - 12;
    const left = isRight ? rect.right / zoom - width : rect.left / zoom;

    setDropdownPos({
      top: rect.bottom / zoom + 5,
      left: Math.max(12, Math.min(left, maxLeft)),
    });
  };

  useEffect(() => {
    if (!isOpenDropDown || budgetCalculator) return;

    const handleResize = () => {
      updateDropdownPosition();
    };

    const handleScroll = (e: Event) => {
      const target = e.target as Node | null;

      if (
        target &&
        ((portalDropDownRef.current &&
          portalDropDownRef.current.contains(target)) ||
          (dropDownRef.current && dropDownRef.current.contains(target)))
      ) {
        return;
      }

      setIsOpenDropDown(false);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [budgetCalculator, isOpenDropDown]);

  const handleStatusChange = (e: any, isCheckbox: any, c: any) => {
    if (isCheckbox) {
      if (e.target.checked) {
        setChecked([...checked, c]);
      } else {
        setChecked(checked.filter((item: any) => item !== c));
      }
    } else {
      const temp = checked.filter((item: any) => item === c);
      if (temp.length > 0) {
        setChecked(checked.filter((item: any) => item !== c));
      } else {
        setChecked([...checked, c]);
      }
    }
  };

  const handleCheckAllChange = (e: any, isCheckbox: any) => {
    if (isCheckbox) {
      if (e.target.checked) {
        const allStatus = options.map((val: any) => val.value);
        setChecked(allStatus);
      } else {
        setChecked([]);
      }
    } else {
      if (checked.length !== options.length) {
        const allStatus = options.map((val: any) => val.value);
        setChecked(allStatus);
      } else {
        setChecked([]);
      }
    }
  };

  const handleOnChangeSearch = (e: any) => {
    setSearchInput(e.target.value);
  };

  const handleDropdownToggle = () => {
    if (!isOpenDropDown && dropDownRef.current) {
      onOpen();
      setTimeout(() => {
        updateDropdownPosition();
        setIsOpenDropDown(true);
      }, 350);
      return;
    }
    setIsOpenDropDown(false);
  };

  const dropDownContent = (
    <>
      {isSearchDisplay && (
        <div className="relative">
          <input
            value={searchInput}
            onChange={handleOnChangeSearch}
            placeholder="Search"
            className="h-[40px] text-sm text-txtPrimary w-full !pl-[40px] border-b-[1px] border-b-gray outline-none rounded-t-[7px]"
          />
          <div className="absolute top-[11px] left-[16px]">
            <SearchIcon />
          </div>
        </div>
      )}
      <div className="h-[40px] flex items-center gap-[7px] pl-[15px] py-[11px] border-b border-b-strokePrimary hover:bg-bgHover">
        <input
          type="checkbox"
          checked={checked.length === options.length}
          onChange={(e) => handleCheckAllChange(e, true)}
          className="dcdial_portal_checkbox"
        />
        <div
          className="text-txtPrimary text-sm font-normal cursor-pointer leading-normal"
          onClick={(e) => handleCheckAllChange(e, false)}
        >
          Select All
        </div>
      </div>
      <div
        className={`${
          isSearchDisplay ? "max-h-[160px]" : "max-h-[200px]"
        } overflow-y-auto`}
      >
        {filterData.map((data: any, i: number) => (
          <div key={i}>
            <div className="flex items-center gap-[7px] h-[40px] px-[15px] py-[11px] leading-normal border-b border-b-strokePrimary hover:bg-bgHover">
              <input
                type="checkbox"
                checked={checked.includes(data.value)}
                onChange={(e) => handleStatusChange(e, true, data.value)}
                className="dcdial_portal_checkbox"
              />
              <div
                className="text-txtPrimary text-sm font-normal cursor-pointer"
                onClick={(e) => handleStatusChange(e, false, data.value)}
              >
                {data.name}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div
      className="relative w-full text-blue hover:!text-btnPrimaryHover"
      ref={dropDownRef}
    >
      {budgetCalculator ? (
        <p
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => setIsOpenDropDown(!isOpenDropDown)}
        >
          <AddCircleIconBlue /> Add new
        </p>
      ) : (
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isOpenDropDown}
          aria-haspopup="listbox"
          aria-label={`${label} filter`}
          className="rounded-[20px] w-full min-w-0 hover:bg-bgHover border-[1px] border-strokePrimary bg-bgPrimary h-[34px] flex items-center gap-[6px] pl-[9px] pr-[15px] cursor-pointer"
          onClick={handleDropdownToggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleDropdownToggle();
            }
          }}
        >
          <div
            role={checked.length > 0 ? "button" : undefined}
            tabIndex={checked.length > 0 ? 0 : undefined}
            aria-label={
              checked.length > 0 ? `Clear ${label} filter` : undefined
            }
            className="cursor-pointer shrink-0"
            onClick={(e) => {
              if (checked.length > 0) {
                e.stopPropagation();
                e.preventDefault();
                setChecked([]);
              }
            }}
            onKeyDown={(e) => {
              if (checked.length > 0 && (e.key === "Enter" || e.key === " ")) {
                e.stopPropagation();
                e.preventDefault();
                setChecked([]);
              }
            }}
          >
            {checked.length > 0 ? <CloseCircleIcon /> : <AddCircleIcon />}
          </div>
          <div className="text-txtParagraph leading-normal font-medium text-xs whitespace-nowrap shrink-0">
            {label}
          </div>
          {checked.length > 0 && (
            <>
              <div className="h-[16px] w-[1px] bg-txtParagraph shrink-0" />
              <div className="lg:block hidden lg:text-sm text-xs font-bold text-txtPrimary leading-normal whitespace-nowrap overflow-hidden truncate">
                {checked.join(", ")}
              </div>
              <div className="block lg:hidden lg:text-sm text-xs font-bold text-txtPrimary leading-normal truncate min-w-0">
                {checked.length > 1
                  ? `${checked[0]} +${checked.length - 1} more`
                  : checked[0]}
              </div>
              {/* <div
                title={checked.join(", ")}
                className="md:hidden block text-sm font-bold text-txtPrimary leading-normal truncate"
              >
                {checked.length > 1 ? checked.length : checked[0]}
              </div> */}
            </>
          )}
        </div>
      )}

      {isOpenDropDown &&
        (budgetCalculator ? (
          <div
            className={`absolute top-[37px] ${
              isRight
                ? "right-[-20px] w-[200px]"
                : "lg:left-0 md2:-left-1  -left-2 lg:w-[200px] w-[190px]"
            } bg-bgPrimary shadow-[0px_0px_12px_-3px_#DFE4EC] z-30 rounded-[7px] border-[1px] border-strokePrimary`}
          >
            {dropDownContent}
          </div>
        ) : (
          ReactDOM.createPortal(
            <div
              ref={portalDropDownRef}
              style={{
                position: "fixed",
                top: dropdownPos.top,
                left: dropdownPos.left,
                zIndex: 9999,
              }}
              className={`scaled-portal ${
                isRight ? "w-[260px]" : "lg:w-[200px] w-[190px]"
              } bg-bgPrimary shadow-[0px_0px_12px_-3px_#DFE4EC] rounded-[7px] border-[1px] border-strokePrimary`}
            >
              {dropDownContent}
            </div>,
            document.body
          )
        ))}
    </div>
  );
};

export default SelectAllDropDown;
